import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

import {
  useAccountSettings,
  useHasAccessClaim,
  useMerchantApiMutation,
  useNotifications,
  useActivityBlocker,
} from "../../hooks";
import {
  IMerchantAccountSettings,
  SwitchAccountModeRequest,
} from "../../interfaces-and-types";
import { View } from "./View";
import { userAccessPolicyConstants } from "../../constants";
import { userHasAccessRight } from "../../utils";

const PREPARING_YOUR_DASHBOARD_MESSAGE = "Preparing your dashboard...";
export interface IAccountModeSwitcherViewProps {
  isLiveMode: boolean;
  isDisabled: boolean;
  hintMessage: string | null;
  toggleAccountMode: () => void;
}

export const AccountModeSwitcher = () => {
  const queryClient = useQueryClient();
  const notificationsHandler = useNotifications();

  const userCanToggleMode = useHasAccessClaim(userAccessPolicyConstants.owner);

  const {
    isLoading,
    isSuccess,
    // data: apiResponse,
    mutate: switchAccountMode,
  } = useMerchantApiMutation<
    SwitchAccountModeRequest,
    IMerchantAccountSettings
  >("switchAccountMode", ["getAccountSettings"]).usePostMutation;

  const toggleAccountMode = () => {
    if (!userCanToggleMode) {
      notificationsHandler.showNotification({
        ariaLabel: "error notification",
        title: "Not Authorized",
        kind: "error",
        message: "You do not have permission to toggle the account mode",
        statusIconDescription: "error",
        timeout: 10000,
      });

      return;
    }

    switchAccountMode({
      is_live: !accountSettings?.is_live,
    });
  };

  const { data: accountSettings, refreshRemoteData: refreshAccountSettings } =
    useAccountSettings();

  const isLive = accountSettings?.is_live || false;

  const { setBlocked, clearBlocked } = useActivityBlocker();

  useEffect(() => {
    if (isSuccess) {
      refreshAccountSettings();
    }
  }, [isSuccess]);

  useEffect(() => {
    // queryClient.clear(); // NOTE: causes constant UI refresh effects
    queryClient.invalidateQueries();
    // queryClient.refetchQueries();
  }, [isLive]);

  let timeoutId = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (isSuccess) {
      setBlocked(PREPARING_YOUR_DASHBOARD_MESSAGE);

      timeoutId.current = setTimeout(() => {
        clearBlocked(true, PREPARING_YOUR_DASHBOARD_MESSAGE);
      }, 2000);
    }

    return () => {
      timeoutId && clearTimeout(timeoutId.current);
    };
  }, [isSuccess]);

  return (
    <View
      isLiveMode={accountSettings?.is_live || false}
      hintMessage={
        !userCanToggleMode
          ? "You are not Authorized to switch account mode"
          : null
      }
      isDisabled={isLoading || !userCanToggleMode}
      toggleAccountMode={toggleAccountMode}
    />
  );
};
